import React, { useMemo } from "react";
import Dialog from "./dialogs";

const InvoicePreviewDialog = ({
    invoice,
    t,
    onClose,
    onPay,
    paying = false,
    payError = null,
}) => {
    const helpers = useMemo(() => {
        const parseInvoiceMeta = (meta) => {
            if (!meta) return {};
            if (typeof meta === "object") return meta;
            try {
                return JSON.parse(meta);
            } catch {
                return {};
            }
        };

        const getPlanDescription = (inv) => {
            const metaObj = parseInvoiceMeta(inv?.meta);
            return inv?.description || metaObj?.plan_description || "";
        };

        const extractPlanFeatures = (meta) => {
            const metaObj = parseInvoiceMeta(meta);
            const features = metaObj?.plan_features;
            if (Array.isArray(features)) {
                return features.filter(Boolean);
            }
            const display = metaObj?.plan_options?.display;
            if (Array.isArray(display)) {
                return display
                    .map((item) => {
                        if (!item) return null;
                        const label = item.label || "";
                        const value = item.value || "";
                        return `${label}${label && value ? ": " : ""}${value}`.trim();
                    })
                    .filter(Boolean);
            }
            const program = metaObj?.plan_options?.program;
            if (program && typeof program === "object") {
                return Object.entries(program)
                    .map(([key, value]) => `${key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}: ${value}`)
                    .filter(Boolean);
            }
            return [];
        };

        const getAmountLabel = (amount, currency) => {
            if (amount === undefined || amount === null || amount === "") return t?.na || "-";
            const num = Number(amount);
            const formatted = Number.isNaN(num) ? amount : num.toFixed(2);
            return `${currency || "$"}${formatted}`;
        };

        const formatDateTime = (value, fallback = t?.na || "-") => {
            if (!value) return fallback;
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return fallback;
            return date.toLocaleString();
        };

        const isInvoicePending = (inv) => ((inv?.status || "").toLowerCase() === "pending");

        const StatusBadge = ({ status }) => {
            const normalized = (status || '').toLowerCase();
            const colors = {
                pending: { background: '#FFC107', color: '#000000' },
                paid: { background: '#4CAF50', color: '#FFFFFF' },
                canceled: { background: '#F44336', color: '#FFFFFF' },
                unknown: { background: '#9E9E9E', color: '#FFFFFF' },
            };
            const label = (t?.statusMap && t.statusMap[normalized]) || t?.statusMap?.unknown || normalized || "Unknown";
            const style = {
                display: 'inline-block',
                padding: '5px 10px',
                borderRadius: 20,
                fontWeight: 'bold',
                fontSize: 14,
                textAlign: 'center',
                ...(colors[normalized] || colors.unknown),
            };
            return <span style={style}>{label}</span>;
        };

        const formatStatus = (inv) => <StatusBadge status={inv?.status} />;

        return {
            parseInvoiceMeta,
            getPlanDescription,
            extractPlanFeatures,
            getAmountLabel,
            formatDateTime,
            formatStatus,
            isInvoicePending,
        };
    }, [t]);

    if (!invoice) return null;

    const planDescription = helpers.getPlanDescription(invoice);
    const planFeatures = helpers.extractPlanFeatures(invoice.meta);

    const status = (invoice.status || "").toLowerCase();
    const statusColors = {
        pending: { background: "#FFC107", text: "#000000" },
        paid: { background: "#4CAF50", text: "#FFFFFF" },
        canceled: { background: "#F44336", text: "#FFFFFF" },
        default: { background: "#6366f1", text: "#FFFFFF" },
    };
    const statusStyle = statusColors[status] || statusColors.default;

    return (
        <Dialog
            title={t.previewTitle}
            editTitle={t.previewTitle}
            customTitle={t.previewTitle}
            fieldsData={{}}
            closeDialog={onClose}
            saveDialog={onClose}
            dialogSize="modal-xl modal-dialog-centered"
            hideActions
            footerContent={
                <div className="d-flex justify-content-end gap-2 w-100">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={onClose}
                    >
                        {t.close || t.maybeLater || "Close"}
                    </button>
                    {helpers.isInvoicePending(invoice) && onPay && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => onPay(invoice)}
                            disabled={paying}
                        >
                            {paying ? (t.creatingInvoice || "Processing…") : (t.payInvoice || "Pay")}
                        </button>
                    )}
                </div>
            }
        >
            {payError && (
                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-4">
                    <i className="ti ti-alert-triangle"></i>
                    <span>{payError}</span>
                </div>
            )}
            <div
                className="rounded-4 p-4 mb-4 text-white"
                style={{ background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 60%, #9333ea 100%)" }}
            >
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
                    <div>
                        <div className="text-uppercase opacity-75 fw-semibold small mb-2">
                            {t.previewStatus || "Status"}
                        </div>
                        <div
                            className="mt-2 fw-semibold rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2"
                            style={{
                                background: statusStyle.background,
                                color: statusStyle.text,
                            }}
                        >
                            {helpers.formatStatus(invoice)}
                        </div>
                    </div>
                    <div className="text-center px-4">
                        <div className="opacity-75 text-uppercase fw-semibold small">
                            {t.previewAmount}
                        </div>
                        <div className="display-5 fw-bold text-white">
                            {helpers.getAmountLabel(invoice.amount ?? invoice.pricing_plan_price, invoice.currency)}
                        </div>
                    </div>
                    <div className="text-end d-flex flex-column align-items-end gap-2">
                        <div className="opacity-75 text-uppercase fw-semibold small">
                            {t.previewDue}
                        </div>
                        <div className="fw-semibold">
                            {helpers.formatDateTime(invoice.due_date, t.na || "-")}
                        </div>
                        <div className="opacity-75 small">
                            {(t.previewIssued || "Issued")}: {helpers.formatDateTime(invoice.issued_at, t.na || "-")}
                        </div>
                    </div>
                </div>
                <div className="mt-3 text-end small opacity-75">
                    {invoice.invoice_code || invoice.id || "-"}
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-7">
                    <div className="border rounded-4 p-4 h-100 shadow-sm bg-white">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <div className="text-uppercase text-muted fw-semibold small">
                                    {t.previewPlan || "Plan"}
                                </div>
                                <div className="d-flex align-items-baseline gap-3 flex-wrap">
                                    <span className="display-6 fw-bold text-primary mb-1">
                                        {invoice.pricing_plan_name || t.noPlan || "-"}
                                    </span>
                                    <span className="text-muted fw-semibold">
                                        {invoice.period || t.na || "-"}
                                    </span>
                                </div>
                            </div>
                            <div className="badge bg-primary-subtle text-primary fs-6 rounded-pill px-3 py-2">
                                {invoice.workspace_name || invoice.workspace_label || invoice.workspace_id || t.na || "-"}
                            </div>
                        </div>

                        {planDescription ? (
                            <p className="mt-3 mb-4 text-body-secondary">
                                {planDescription}
                            </p>
                        ) : null}

                        {planFeatures.length ? (
                            <div>
                                <div className="text-uppercase text-muted fw-semibold fs-2 mb-2">{t.previewFeatures || "Plan options"}</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {planFeatures.map((feature, idx) => (
                                        <span
                                            key={`preview-feature-${idx}`}
                                            className="badge rounded-pill bg-primary-subtle text-primary d-inline-flex align-items-center gap-1 px-3 py-2"
                                        >
                                            <i className="ti ti-check"></i>
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="border rounded-4 p-4 mb-4 shadow-sm bg-white">
                        <div className="text-uppercase text-muted fw-semibold fs-2 mb-3">
                            {t.previewWorkspace || "Workspace"}
                        </div>
                        <div className="display-6 fw-bold text-primary mb-2">
                            {invoice.workspace_name || invoice.workspace_label || invoice.workspace_id || t.na || "-"}
                        </div>
                        <div className="text-muted small">
                            {helpers.parseInvoiceMeta(invoice.meta)?.customer || helpers.parseInvoiceMeta(invoice.meta)?.workspace_label || ""}
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default InvoicePreviewDialog;
